import { queryOptions, useQuery } from '@tanstack/react-query';

import { getApiClient, overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { RbacServiceAPIListRolesTypeEnum } from '@cast/types-organization-management';

export const RBAC_ROLES_QUERY_KEY = 'rbac-roles';
const getRbacRolesKey = (params: {
  organizationId: string;
  type: RbacServiceAPIListRolesTypeEnum;
}) => [RBAC_ROLES_QUERY_KEY, params.organizationId, params.type] as const;

export const getRbacRolesQueryOptions = ({
  organizationId,
  type,
  enabled = true,
}: {
  organizationId: string;
  type: RbacServiceAPIListRolesTypeEnum;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getRbacRolesKey({ organizationId, type }),
    queryFn: async () => {
      const { data } = await getApiClient().rbac.listRoles(
        {
          organizationId,
          pageLimit: '9999',
          type,
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );
      return data.roles ?? [];
    },
    enabled: enabled && !!organizationId,
  });

type UseRbacRolesQueryProps = {
  enabled?: boolean;
  type?: RbacServiceAPIListRolesTypeEnum;
  organizationId?: string;
};

export const useRbacRolesQuery = ({
  enabled = true,
  type,
  organizationId: overrideOrganizationId,
}: UseRbacRolesQueryProps = {}) => {
  const { currentOrganization, enterpriseOrgId } = useOrganizations();
  const organizationId =
    overrideOrganizationId ?? enterpriseOrgId ?? currentOrganization?.id;
  /**
   * Selects the appropriate set of roles (enterprise or default) based on the
   * context of the `currentOrganization`.
   *
   * - **Default Behavior:**
   * - If `currentOrganization` is in an enterprise context, `enterprise` roles are used.
   * - If `currentOrganization` is in a default context, `default` roles are used.
   *
   * - **Override:**
   * - To handle exceptional cases, you can explicitly specify the role type
   * ("enterprise" or "default") by passing the `type` prop, which will
   * bypass the default context-based logic.
   */
  const roleType =
    organizationId === enterpriseOrgId
      ? RbacServiceAPIListRolesTypeEnum.ENTERPRISE
      : RbacServiceAPIListRolesTypeEnum.DEFAULT;

  return useQuery(
    getRbacRolesQueryOptions({
      organizationId: organizationId ?? '',
      type: type ?? roleType,
      enabled,
    })
  );
};
